import { useLazyQuery } from '@apollo/client';
import debounce from 'debounce-promise';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
	ArrayElement, AsyncComboboxAutocomplete, handleErrorNotification, isReddit, isSpotify,
} from '@aleph/shared-components';
import { ComboboxAutocompleteOption } from 'wb-reddit/app/common/components/comboboxAutocompleteOption';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import {
	AgencyAccessOptionsDocument,
	AgencyAccessOptionsQueryItems,
	AgencyAccessOptionsQueryUserGroups,
	AgencyAccessOptionsQueryUsersUsers,
	UserAccountLevel,
	UserGroupRole,
} from 'wb-reddit/graphql/generated';
import {
	AuthGraphAccountAccessOptionsDocument,
	UserAccountLevel as AuthGraphUserAccountLevel,
	UserGroupAccessRole,
} from 'wb-spotify/graphql/authgraph/generated';
import { captureException } from '@sentry/react';

type Option = AgencyAccessOptionsQueryItems | AgencyAccessOptionsQueryUsersUsers;

interface IProps {
	disabled: boolean
	onOptionClick: (option: Option) => void
	selectedGroupIds: string[]
}

function AdvertiserAccessAutocomplete({ disabled, onOptionClick, selectedGroupIds }: IProps) {
	const { t } = useTranslation();

	const [searchAccessOptions] = useLazyQuery(
		isReddit()
			? AgencyAccessOptionsDocument
			: AuthGraphAccountAccessOptionsDocument,
	);

	const platformUser = useUserMetaData();

	const debouncedSearchAccessOptions = useMemo(() => debounce(searchAccessOptions, 300), []);

	const isUserOwnerOrAdminOfGroup = (group: ArrayElement<AgencyAccessOptionsQueryUserGroups['items']>) => {
		if (isSpotify()) {
			// @ts-ignore
			if (platformUser?.userAccountLevel === AuthGraphUserAccountLevel.PlatformAdmin) {
				return true;
			}

			// @ts-ignore
			return group?.userGroupAccess?.some(item => [UserGroupAccessRole.Admin, UserGroupAccessRole.Owner].includes(item?.userGroupAccessRole)
				&& item?.user?.userId === platformUser?.userId);
		}

		if (platformUser?.userAccountLevel === UserAccountLevel.Admin) {
			return true;
		}

		return group?.users?.some(
			groupUser => groupUser?.user?.id === platformUser?.userId
				&& [UserGroupRole.Admin, UserGroupRole.Owner].includes(groupUser.userGroupUserRole),
		);
	};

	const loadAccessOptions = async (search: string) => {
		if (!search) return [];
		const platformEmail = platformUser?.userEmail?.split('@')?.[1];
		const [searchName, searchEmail] = search?.split('@') ?? [];

		const isUserAdvertiser = platformUser?.userAccountLevel === UserAccountLevel.Advertiser
			|| platformUser?.userAccountLevel === UserAccountLevel.Admin;

		const variables = isReddit() ? {
			emailSearch: (platformUser?.userAccountLevel === UserAccountLevel.Admin)
				? '*'
				: `*@${platformEmail ?? ''}`,
			userSearch: isUserAdvertiser
				? search
				: `${searchName}@${searchEmail === platformEmail ? searchEmail : ''}`,
			groupSearch: `*${search}*`,
			userLevel:   (isUserAdvertiser && searchEmail !== platformEmail)
				? [UserAccountLevel.Agency, UserAccountLevel.Advertiser, UserAccountLevel.Admin]
				: [
					UserAccountLevel.Advertiser,
					UserAccountLevel.Admin,
					UserAccountLevel.Agency,
					UserAccountLevel.User,
					UserAccountLevel.ReadonlyUser,
				],
			selectedGroupIds,
		} : {
			search,
			selectedGroupIds,
		};

		// @ts-ignore
		const { data, error } = await debouncedSearchAccessOptions({ variables });

		if (error) {
			captureException(error);

			handleErrorNotification(
				error,
				t(
					're: advertiser access autocomplete error',
					'Something went wrong while searching for access options',
				),
			);

			return [];
		}

		if (isReddit()) {
			return [
				...(data?.userGroups?.items ?? []).filter(isUserOwnerOrAdminOfGroup),
				// @ts-ignore
				...data?.users ?? [],
			];
		}

		return [
			// @ts-ignore
			...(data?.users?.items ?? []).flatMap(
				user => user?.userGroupAccess
					?.map(group => group?.userGroup)
					// BE can return group userGroup as null
					?.filter((group) => Boolean(group))
					// @ts-ignore: TODO: type issue
					?.filter(isUserOwnerOrAdminOfGroup),
			),
			...(data?.userGroups?.items ?? []).filter(isUserOwnerOrAdminOfGroup),
		];
	};

	return (
		<AsyncComboboxAutocomplete
			loadOptions={loadAccessOptions}
			renderOption={({ option, searchQuery, onSelected }) => (
				<ComboboxAutocompleteOption
					option={option}
					onOptionClick={onOptionClick}
					onSelected={onSelected}
					searchQuery={searchQuery}
				/>
			)}
			NoOptionsComponent={() => (
				<div className='max-w-[25vw] w-fit'>
					<strong>{t('sc: no options found', 'No options found')}</strong>
				</div>
			)}
			placeholder={t('re: add users or teams', 'Add users or teams')}
			disabled={disabled}
		/>
	);
}

export default AdvertiserAccessAutocomplete;
